import { Eye, EyeOff, ShieldCheck, Terminal } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { InputField } from '@/components/ui/field';
import { ChargementBloc, Encart, EtatErreur } from '@/components/ui/feedback';
import { Logo } from '@/components/ui/logo';
import { ApiError } from '@/lib/api-client';
import { useConnexion, useEtatAuth } from './api/auth.api';
import { useSession } from './useSession';

/** Où retourner après coup — posé par le garde de route. */
type EtatNavigation = { de?: string };

function Cadre({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-surface-sunken px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <Logo ton="clair" taille="lg" />
        </div>
        <div className="rounded-card border border-line bg-white p-6 shadow-card">{children}</div>
        <p className="mt-6 text-center text-xs text-ink-muted">
          Gestion de stock et facturation — Salé, Maroc
        </p>
      </div>
    </div>
  );
}

export function ConnexionPage() {
  const etat = useEtatAuth();
  const session = useSession();
  const connexion = useConnexion();
  const naviguer = useNavigate();
  const emplacement = useLocation();

  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [visible, setVisible] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const destination = (emplacement.state as EtatNavigation | null)?.de ?? '/';

  // Déjà connecté : rien à faire ici.
  if (session) return <Navigate to={destination} replace />;

  if (etat.isPending) {
    return (
      <Cadre>
        <ChargementBloc libelle="Vérification…" />
      </Cadre>
    );
  }

  if (etat.isError) {
    return (
      <Cadre>
        <EtatErreur erreur={etat.error} onReessayer={() => void etat.refetch()} />
      </Cadre>
    );
  }

  /* Le serveur délègue à Cloudflare Access, ou n'exige rien : afficher un
   * formulaire ici induirait en erreur — il n'aurait personne à qui parler. */
  if (etat.data.mode !== 'session') {
    return (
      <Cadre>
        <div className="text-center">
          <div className="mx-auto mb-3 w-fit rounded-full bg-mustard-50 p-3 text-mustard-600">
            <ShieldCheck aria-hidden className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-ink">
            {etat.data.mode === 'access'
              ? "L'accès est géré par Cloudflare Access"
              : 'Aucune authentification requise'}
          </p>
          <p className="mt-1 text-sm text-ink-muted">
            {etat.data.mode === 'access'
              ? "Cet écran ne sert pas ici : Cloudflare affiche son propre formulaire avant même que l'application se charge."
              : "Le serveur tourne en mode développement (DEV_SKIP_AUTH), toutes les requêtes passent."}
          </p>
          <Button className="mt-4" variant="primary" onClick={() => naviguer('/', { replace: true })}>
            Ouvrir l'application
          </Button>
        </div>
      </Cadre>
    );
  }

  /* Base neuve : le formulaire refuserait tout. Autant donner la marche à
   * suivre — c'est exactement le moment où on la cherche. */
  if (!etat.data.configure) {
    return (
      <Cadre>
        <div className="mb-4 flex items-center gap-2 text-ink">
          <Terminal aria-hidden className="h-5 w-5 text-mustard-600" />
          <h1 className="text-base font-semibold">Aucun compte n'existe encore</h1>
        </div>
        <p className="text-sm text-ink-muted">
          Les comptes se créent depuis le poste d'administration, pas depuis cette page : une
          inscription ouverte sur Internet reviendrait à confier la boutique au premier venu.
        </p>
        <pre className="mt-3 overflow-x-auto rounded-md bg-ink px-3 py-2.5 text-xs text-white">
          pnpm compte
        </pre>
        <p className="mt-2 text-xs text-ink-muted">
          La commande demande l'adresse e-mail et le mot de passe, puis les inscrit dans la base.
          Ajoutez <code className="font-mono">:local</code> pour la base de développement.
        </p>
        <Button className="mt-4 w-full" variant="outline" onClick={() => void etat.refetch()}>
          J'ai créé le compte
        </Button>
      </Cadre>
    );
  }

  const soumettre = (e: FormEvent) => {
    e.preventDefault();
    setErreur(null);

    connexion.mutate(
      { email: email.trim().toLowerCase(), mot_de_passe: motDePasse },
      {
        onSuccess: () => naviguer(destination, { replace: true }),
        onError: (e2: unknown) => {
          setMotDePasse('');
          setErreur(
            e2 instanceof ApiError ? e2.message : 'Connexion impossible. Réessayez dans un instant.',
          );
        },
      },
    );
  };

  return (
    <Cadre>
      <h1 className="text-lg font-semibold tracking-tight text-ink">Connexion</h1>
      <p className="mt-1 text-sm text-ink-muted">Accédez à la gestion de la boutique.</p>

      <form className="mt-5 space-y-4" onSubmit={soumettre}>
        <InputField
          label="Adresse e-mail"
          type="email"
          name="email"
          autoComplete="username"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <div className="relative">
          <InputField
            label="Mot de passe"
            type={visible ? 'text' : 'password'}
            name="mot_de_passe"
            autoComplete="current-password"
            required
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            className="[&_input]:pr-10"
          />
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            // Le libellé décrit l'action, pas l'état : c'est ce qu'annonce un lecteur d'écran.
            aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            className="absolute right-2 top-[30px] rounded p-1.5 text-ink-muted hover:text-ink"
          >
            {visible ? (
              <EyeOff aria-hidden className="h-4 w-4" />
            ) : (
              <Eye aria-hidden className="h-4 w-4" />
            )}
          </button>
        </div>

        {erreur ? (
          <Encart ton="danger">
            <span role="alert">{erreur}</span>
          </Encart>
        ) : null}

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          chargement={connexion.isPending}
          disabled={email.trim() === '' || motDePasse === ''}
        >
          Se connecter
        </Button>
      </form>
    </Cadre>
  );
}
